import { motion } from 'framer-motion';
import { AlertCircle, Lock, Mail } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { isUserCoach, isUserAdmin } from '../../lib/auth';

export function SILoginPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [credentials, setCredentials] = useState({ email: '', password: '' });

  // Sign out any existing session on mount so login is always fresh
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        supabase.auth.signOut().then(() => setIsReady(true));
      } else {
        setIsReady(true);
      }
    });
  }, []);

  // Redirect authenticated coaches/admins
  useEffect(() => {
    if (isReady && user?.id) {
      Promise.all([isUserCoach(user.id), isUserAdmin(user.id)]).then(([coach, admin]) => {
        if (admin) navigate('/si/admin', { replace: true });
        else if (coach) navigate('/si/app/dashboard', { replace: true });
      });
    }
  }, [user, isReady, navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email: credentials.email,
        password: credentials.password,
      });

      if (signInError) throw signInError;
      if (!data.user) throw new Error('Login failed');

      const [isCoach, admin] = await Promise.all([
        isUserCoach(data.user.id),
        isUserAdmin(data.user.id),
      ]);

      if (!isCoach && !admin) {
        await supabase.auth.signOut();
        throw new Error('No Session Intelligence account found for this email. Please register first.');
      }

      navigate(admin ? '/si/admin' : '/si/app/dashboard', { replace: true });
    } catch (err: any) {
      if (err.message?.includes('Invalid login credentials')) {
        setError('Invalid email or password. Please try again.');
      } else {
        setError(err.message || 'Failed to sign in. Please try again.');
      }
      await supabase.auth.signOut();
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const { error: fnError } = await supabase.functions.invoke('reset-password', {
        body: { email: resetEmail, locale: 'en' },
      });
      if (fnError) throw fnError;
      setResetSent(true);
    } catch (err: any) {
      setError(err.message || 'Failed to send reset email. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-5 flex flex-col">
      {/* Top bar */}
      <div className="py-5 px-6 flex items-center justify-between max-w-5xl mx-auto w-full">
        <Link to="/" className="flex items-center">
          <span className="text-sm font-semibold text-gray-80">Session Intelligence<sup className="text-[0.6em]">™</sup></span>
        </Link>
        <Link to="/si/register" className="text-sm font-medium text-gray-50 hover:text-gray-80 transition-colors">
          Create account
        </Link>
      </div>

      {/* Card */}
      <div className="flex-1 flex items-center justify-center px-4 pb-16">
        <div className="w-full max-w-md">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <h1 className="text-3xl font-bold text-gray-80 mb-2">
              {showForgotPassword ? 'Reset your password' : 'Welcome back'}
            </h1>
            <p className="text-gray-50">
              {showForgotPassword
                ? 'Enter your email and we\'ll send you a new password'
                : 'Sign in to your Session Intelligence™ account'}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.08 }}
            className="bg-white rounded-3xl border border-gray-20 shadow-sm p-8"
          >
            {error && (
              <div className="flex items-center gap-2 bg-red-50 text-red-700 p-4 rounded-2xl mb-6 text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {error}
              </div>
            )}

            {resetSent ? (
              <div className="text-center py-4">
                <div className="w-12 h-12 bg-success-10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-6 h-6 text-success-50" />
                </div>
                <h3 className="font-semibold text-gray-80 mb-2">Check your email</h3>
                <p className="text-gray-50 text-sm mb-6">We've sent your new login details.</p>
                <button
                  onClick={() => { setShowForgotPassword(false); setResetSent(false); }}
                  className="text-success-50 font-medium text-sm hover:underline"
                >
                  Back to sign in
                </button>
              </div>
            ) : showForgotPassword ? (
              <form onSubmit={handleForgotPassword} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-80 mb-1.5">Email address</label>
                  <input
                    type="email" value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    className="w-full px-4 py-3 rounded-2xl border border-gray-20 bg-brand-5 text-gray-80 placeholder-gray-40 focus:outline-none focus:border-success-40 transition-colors text-sm"
                    placeholder="you@example.com" required disabled={isLoading}
                  />
                </div>
                <div className="flex justify-between items-center pt-2">
                  <button type="button" onClick={() => setShowForgotPassword(false)}
                    className="text-sm text-gray-50 hover:text-gray-80 transition-colors"
                  >
                    Back to sign in
                  </button>
                  <button type="submit" disabled={isLoading}
                    className="btn-primary text-sm px-5 py-2.5"
                  >
                    {isLoading ? 'Sending…' : 'Send reset link'}
                  </button>
                </div>
              </form>
            ) : (
              <form onSubmit={handleLogin} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-80 mb-1.5">Email address</label>
                  <input
                    type="email" value={credentials.email}
                    onChange={(e) => setCredentials(p => ({ ...p, email: e.target.value }))}
                    className="w-full px-4 py-3 rounded-2xl border border-gray-20 bg-brand-5 text-gray-80 placeholder-gray-40 focus:outline-none focus:border-success-40 transition-colors text-sm"
                    placeholder="you@example.com" required disabled={isLoading}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-80 mb-1.5">Password</label>
                  <input
                    type="password" value={credentials.password}
                    onChange={(e) => setCredentials(p => ({ ...p, password: e.target.value }))}
                    className="w-full px-4 py-3 rounded-2xl border border-gray-20 bg-brand-5 text-gray-80 placeholder-gray-40 focus:outline-none focus:border-success-40 transition-colors text-sm"
                    placeholder="••••••••" required disabled={isLoading}
                  />
                </div>
                <div className="flex justify-between items-center pt-2">
                  <button type="button" onClick={() => setShowForgotPassword(true)}
                    className="text-sm text-gray-50 hover:text-gray-80 transition-colors"
                  >
                    Forgot password?
                  </button>
                  <button type="submit" disabled={isLoading}
                    className="btn-primary text-sm px-5 py-2.5 inline-flex items-center gap-2"
                  >
                    <Lock className="w-4 h-4" />
                    {isLoading ? 'Signing in…' : 'Sign In'}
                  </button>
                </div>
              </form>
            )}
          </motion.div>

          <p className="text-center text-sm text-gray-50 mt-6">
            Don't have an account?{' '}
            <Link to="/si/register" className="text-success-50 font-medium hover:underline">
              Request access
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
